import {
    DataTableCustomized,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getServiceAnalyticUsages } from '@/modules/Service/services'
import { ServiceMonitoringProps } from '@/modules/Service/types'
import { formatBinaryStorageUnit, formatNumber } from '@/utils/function'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { Select, Typogaphy } from 'vnetwork-ui'

// Wasabi bills stored volume (GiB-month, binary 1024³), reconciled per calendar month. The console
// retains a bounded daily history, so the picker offers the trailing 12 months; each
// maps to an absolute [from,to] the backend folds server-side. Mirrors Partner's OSS
// Business Usage page (month picker + KPI tiles + per-bucket table).

interface StorageBucketRow {
    bucket: string
    peak_stored_bytes: number
    avg_stored_bytes: number
    billable_objects: number
    deleted_bytes: number
}
interface StorageSummary {
    peak_stored_bytes: number
    avg_stored_bytes: number
    billable_objects: number
    deleted_bytes: number
    // Billed figure in binary GiB from @mgmt (peak_stored_bytes / 1024³, the basis
    // datacentral bills storage on). `gb_month` is the legacy decimal-GB field name,
    // kept optional as a fallback during deploy skew before @mgmt ships `gib_month`.
    gib_month?: number
    gb_month?: number
}
interface StorageBusiness {
    summary?: StorageSummary
    buckets?: StorageBucketRow[]
}

// Full binary units (TiB/GiB) to match Partner's OSS Business Usage page — the
// `unitOutput` short form (T/G) diverged from what the customer sees.
const fmtBytes = (v?: number) =>
    formatBinaryStorageUnit(v ?? 0, 2, 'XiB', false)

function buildMonths() {
    const now = dayjs()
    return Array.from({ length: 12 }, (_, i) => {
        const d = now.subtract(i, 'month').startOf('month')
        const next = d.add(1, 'month')
        const to = next.isAfter(now) ? now : next
        return {
            key: d.format('YYYY-MM'),
            label: d.format('MMM YYYY'),
            from: d.toISOString(),
            to: to.toISOString(),
        }
    })
}

const bucketColumns: DataTableColumn<StorageBucketRow>[] = [
    { key: 'bucket', title: 'Bucket' },
    {
        // Company policy bills the peak — show it first as the billed figure.
        key: 'peak_stored_bytes',
        title: 'Peak stored (billed)',
        render: (_v, r) => fmtBytes(r.peak_stored_bytes),
    },
    {
        key: 'avg_stored_bytes',
        title: 'Avg stored',
        render: (_v, r) => fmtBytes(r.avg_stored_bytes),
    },
    {
        key: 'billable_objects',
        title: 'Objects',
        render: (_v, r) => formatNumber(r.billable_objects),
    },
    {
        key: 'deleted_bytes',
        title: 'Deleted',
        render: (_v, r) => fmtBytes(r.deleted_bytes),
    },
]

const Monitoring: React.FC<ServiceMonitoringProps> = ({ service }) => {
    const serviceID = service?.service_id as number
    const months = useMemo(buildMonths, [])
    const [month, setMonth] = useState(months[0].key)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const range = months.find((m) => m.key === month) ?? months[0]

    const { data } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES, serviceID, 'storage-business', month],
        queryFn: () =>
            getServiceAnalyticUsages({
                serviceID,
                params: { type: '/storage', gte: range.from, lte: range.to },
            }),
        enabled: !!serviceID,
    })

    const biz = (data ?? {}) as StorageBusiness
    const summary = biz.summary
    const buckets = biz.buckets ?? []

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase()
        return q
            ? buckets.filter((b) => b.bucket?.toLowerCase().includes(q))
            : buckets
    }, [buckets, search])
    const paged = rows.slice((page - 1) * pageSize, page * pageSize)

    const kpis = [
        {
            label: 'Peak stored (billed)',
            value: fmtBytes(summary?.peak_stored_bytes),
            hint: `${formatNumber(Math.round(summary?.gib_month ?? summary?.gb_month ?? 0))} GiB billed`,
        },
        {
            label: 'Avg stored',
            value: fmtBytes(summary?.avg_stored_bytes),
            hint: '',
        },
        {
            label: 'Billable objects',
            value: formatNumber(summary?.billable_objects ?? 0),
            hint: '',
        },
        { label: 'Buckets', value: formatNumber(buckets.length), hint: '' },
    ]

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Typogaphy size="medium" className="text-primary font-semibold">
                    Business Usage
                </Typogaphy>
                <div className="w-[180px]">
                    <Select
                        value={month}
                        onChange={(v) => {
                            setMonth(v as string)
                            setPage(1)
                        }}
                        options={months.map((m) => ({
                            label: m.label,
                            value: m.key,
                        }))}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {kpis.map((k) => (
                    <div key={k.label} className="bg-white rounded-[8px] p-4">
                        <div className="text-xs text-gray-500">{k.label}</div>
                        <div className="text-lg font-semibold text-primary">
                            {k.value}
                        </div>
                        {k.hint && (
                            <div className="text-xs text-gray-400">
                                {k.hint}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-[8px] p-4">
                <DataTableCustomized<StorageBucketRow>
                    columns={bucketColumns}
                    rows={paged}
                    rowKey={(row) => row.bucket}
                    size="small"
                    bordered
                    emptyMessage="No data"
                    toolbar={{
                        search: {
                            value: search,
                            onChange: (value) => {
                                setSearch(value)
                                setPage(1)
                            },
                            placeholder: 'Search bucket...',
                        },
                    }}
                    pagination={{
                        page,
                        pageSize,
                        total: rows.length,
                        onPageChange: setPage,
                        onPageSizeChange: setPageSize,
                    }}
                />
            </div>
        </div>
    )
}

export default Monitoring
